import React from 'react';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  CheckCircle2,
  Clock3,
  Gauge,
  ReceiptText,
  Scale,
  TrendingDown,
  TrendingUp,
  WalletCards,
} from 'lucide-react';

const formatPercentage = (value, decimals = 1) => {
  const number = Number(value || 0);
  return `${number.toFixed(decimals)}%`;
};

const MetricRow = ({
  label,
  value,
  valueClass = 'text-gray-900',
  icon: Icon,
}) => (
  <div className="flex items-center justify-between gap-4 py-2.5">
    <div className="flex min-w-0 items-center gap-2 text-sm text-gray-600">
      {Icon && <Icon className="h-4 w-4 shrink-0 text-gray-400" />}
      <span>{label}</span>
    </div>
    <span className={`shrink-0 text-sm font-semibold ${valueClass}`}>
      {value}
    </span>
  </div>
);

const ComparisonRow = ({
  label,
  current,
  previous,
  change,
  formatCurrency,
  isPercentage = false,
  changeInPoints = false,
}) => {
  const numericChange =
    change == null || Number.isNaN(Number(change)) ? null : Number(change);

  const positive = numericChange != null && numericChange > 0;
  const negative = numericChange != null && numericChange < 0;
  const ChangeIcon = positive ? ArrowUpRight : negative ? ArrowDownRight : null;

  const formatValue = (value) =>
    isPercentage
      ? formatPercentage(value)
      : formatCurrency(value);

  return (
    <div className="grid grid-cols-1 gap-2 border-t border-gray-100 py-3 first:border-t-0 sm:grid-cols-[1.3fr_1fr_1fr_auto] sm:items-center">
      <div className="text-sm font-medium text-gray-700">{label}</div>

      <div className="text-sm">
        <span className="mr-1 text-gray-500 sm:hidden">Atual:</span>
        <span className="font-semibold text-gray-900">
          {formatValue(current)}
        </span>
      </div>

      <div className="text-sm">
        <span className="mr-1 text-gray-500 sm:hidden">Anterior:</span>
        <span className="text-gray-600">{formatValue(previous)}</span>
      </div>

      <div>
        {numericChange == null ? (
          <span className="text-xs text-gray-500">Sem comparação</span>
        ) : (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
              positive
                ? 'bg-emerald-50 text-emerald-700'
                : negative
                ? 'bg-red-50 text-red-700'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {ChangeIcon && <ChangeIcon className="h-3.5 w-3.5" />}
            {numericChange === 0
              ? 'Estável'
              : `${Math.abs(numericChange).toFixed(1)}${
                  changeInPoints ? ' p.p.' : '%'
                }`}
          </span>
        )}
      </div>
    </div>
  );
};

const FinancialSummary = ({ financialReport, formatCurrency }) => {
  if (!financialReport) {
    return null;
  }

  const comparison = financialReport.comparison || {};
  const collectionRate = Number(financialReport.collection_rate || 0);
  const operatingResult = Number(financialReport.operating_result || 0);
  const operatingMargin = Number(financialReport.operating_margin || 0);
  const overdue = Number(financialReport.total_overdue || 0);
  const pending = Number(financialReport.total_pending || 0);
  const payable = Number(financialReport.total_accounts_payable || 0);
  const overdueExpenses = Number(financialReport.total_expenses_overdue || 0);
  const projectedCashBalance = Number(
    financialReport.projected_cash_balance || 0
  );

  const status =
    overdue > 0 || overdueExpenses > 0
      ? {
          label: 'Atenção necessária',
          description:
            'Existem valores vencidos que merecem acompanhamento.',
          icon: AlertTriangle,
          className: 'border-amber-200 bg-amber-50 text-amber-900',
        }
      : operatingResult >= 0
      ? {
          label: 'Situação saudável',
          description:
            'O resultado operacional do período está positivo.',
          icon: CheckCircle2,
          className: 'border-emerald-200 bg-emerald-50 text-emerald-900',
        }
      : {
          label: 'Resultado negativo',
          description:
            'As despesas pagas superaram os recebimentos no período.',
          icon: AlertTriangle,
          className: 'border-red-200 bg-red-50 text-red-900',
        };

  const StatusIcon = status.icon;

  return (
    <section className="space-y-4">
      <div className={`rounded-xl border p-4 ${status.className}`}>
        <div className="flex items-start gap-3">
          <StatusIcon className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">{status.label}</p>
            <p className="mt-1 text-sm">{status.description}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-emerald-50 p-2 text-emerald-700">
              <WalletCards className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Recebimentos</h3>
              <p className="text-xs text-gray-500">
                Cobrança e valores em aberto
              </p>
            </div>
          </div>

          <div className="mt-4 divide-y divide-gray-100">
            <MetricRow
              label="Total a receber"
              value={formatCurrency(pending)}
              icon={Clock3}
              valueClass="text-amber-700"
            />
            <MetricRow
              label="Valores vencidos"
              value={formatCurrency(overdue)}
              icon={AlertTriangle}
              valueClass={overdue > 0 ? 'text-red-700' : 'text-gray-900'}
            />
            <MetricRow
              label="Taxa de cobrança"
              value={formatPercentage(collectionRate)}
              icon={Gauge}
              valueClass={
                collectionRate >= 90
                  ? 'text-emerald-700'
                  : collectionRate >= 75
                  ? 'text-amber-700'
                  : 'text-red-700'
              }
            />
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-red-50 p-2 text-red-700">
              <ReceiptText className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Despesas</h3>
              <p className="text-xs text-gray-500">
                Obrigações e pagamentos
              </p>
            </div>
          </div>

          <div className="mt-4 divide-y divide-gray-100">
            <MetricRow
              label="Despesas pagas"
              value={formatCurrency(financialReport.total_expenses_paid)}
              icon={Banknote}
              valueClass="text-red-700"
            />
            <MetricRow
              label="Contas a pagar"
              value={formatCurrency(payable)}
              icon={Clock3}
              valueClass="text-amber-700"
            />
            <MetricRow
              label="Despesas vencidas"
              value={formatCurrency(overdueExpenses)}
              icon={AlertTriangle}
              valueClass={
                overdueExpenses > 0 ? 'text-red-700' : 'text-gray-900'
              }
            />
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <div
              className={`rounded-lg p-2 ${
                operatingResult >= 0
                  ? 'bg-blue-50 text-blue-700'
                  : 'bg-red-50 text-red-700'
              }`}
            >
              {operatingResult >= 0 ? (
                <TrendingUp className="h-5 w-5" />
              ) : (
                <TrendingDown className="h-5 w-5" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Desempenho</h3>
              <p className="text-xs text-gray-500">
                Eficiência financeira do período
              </p>
            </div>
          </div>

          <div className="mt-4 divide-y divide-gray-100">
            <MetricRow
              label="Ticket médio"
              value={formatCurrency(financialReport.average_ticket)}
              icon={Banknote}
            />
            <MetricRow
              label="Margem operacional"
              value={formatPercentage(operatingMargin)}
              icon={Scale}
              valueClass={
                operatingMargin >= 0 ? 'text-blue-700' : 'text-red-700'
              }
            />
            <MetricRow
              label="Saldo projetado em 30 dias"
              value={formatCurrency(projectedCashBalance)}
              icon={projectedCashBalance >= 0 ? TrendingUp : TrendingDown}
              valueClass={
                projectedCashBalance >= 0
                  ? 'text-blue-700'
                  : 'text-red-700'
              }
            />
          </div>
        </div>
      </div>

      {comparison.available && (
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">
                Comparativo com o período anterior
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Evolução em relação a um intervalo anterior de mesma duração.
              </p>
            </div>

            <div className="hidden grid-cols-3 gap-8 text-xs font-semibold uppercase tracking-wide text-gray-500 sm:grid">
              <span>Atual</span>
              <span>Anterior</span>
              <span>Variação</span>
            </div>
          </div>

          <div className="mt-4">
            <ComparisonRow
              label="Faturamento"
              current={financialReport.total_invoiced}
              previous={comparison.previous_invoiced}
              change={comparison.invoiced_change_percentage}
              formatCurrency={formatCurrency}
            />
            <ComparisonRow
              label="Recebimentos"
              current={financialReport.total_received}
              previous={comparison.previous_received}
              change={comparison.received_change_percentage}
              formatCurrency={formatCurrency}
            />
            <ComparisonRow
              label="Ticket médio"
              current={financialReport.average_ticket}
              previous={comparison.previous_average_ticket}
              change={comparison.average_ticket_change_percentage}
              formatCurrency={formatCurrency}
            />
            <ComparisonRow
              label="Taxa de cobrança"
              current={financialReport.collection_rate}
              previous={comparison.previous_collection_rate}
              change={comparison.collection_rate_change_points}
              formatCurrency={formatCurrency}
              isPercentage
              changeInPoints
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default FinancialSummary;
